import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import ChevronDown from '@decisiv/iconix/lib/components/ChevronDown';
import { Tooltip } from '@decisiv/ui-components';
import { t } from '@lingui/macro';
import UserAvatar from './UserAvatar';
import FieldName from './FieldName';
import CategoryBadge from './CategoryBadge';
import SmartDiff from './SmartDiff';
import CopyButton from './CopyButton';
import { formatValue, propagationSummary } from './utils';
import { formatDayAndTime, timeAgo } from '../../utils/dates';

export const GRID_COLUMNS =
  'minmax(100px, 1fr) 48px minmax(100px, 1.5fr) minmax(160px, 4fr) 80px 28px';

const EXPANDED_BG =
  'color-mix(in srgb, var(--if-accent) 6%, var(--if-surface-1))';

const Row = styled.div`
  display: grid;
  grid-template-columns: ${GRID_COLUMNS};
  gap: 0;
  padding: 10px 14px;
  align-items: center;
  cursor: pointer;
  background: ${({ isEven, isExpanded }) => {
    if (isExpanded) return EXPANDED_BG;
    return isEven ? 'var(--if-surface-1)' : 'var(--if-surface-2)';
  }};
  border-top: ${({ showSessionBorder }) =>
    showSessionBorder ? '1px solid var(--if-border)' : 'none'};
  transition: background 0.1s ease;

  &:hover {
    background: var(--if-accent-soft);
  }
`;

const Cell = styled.div`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Timestamp = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: var(--if-ink-1);
`;

const RelativeTime = styled.div`
  font-size: 10px;
  color: var(--if-ink-3);
`;

const LIFECYCLE_LABELS = {
  enabled: 'Location Enabled',
  disabled: 'Location Disabled',
  removed: 'Location Removed',
  restored: 'Location Restored',
};

const LIFECYCLE_COLORS = {
  enabled: 'var(--if-diff-add-ink)',
  disabled: 'var(--if-warning-ink)',
  removed: 'var(--if-danger-ink)',
  restored: 'var(--if-diff-add-ink)',
};

const LifecycleEventLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${({ event }) => LIFECYCLE_COLORS[event] ?? 'var(--if-danger-ink)'};
  display: block;
`;

const SrmAccountId = styled.span`
  font-size: 10px;
  color: var(--if-ink-3);
  display: block;
`;

// The version is attributed to the acting user, so a propagated change would
// otherwise be indistinguishable from one made by hand in this integration.
const PropagatedBadge = styled.span`
  display: inline-block;
  margin-top: 2px;
  padding: 1px 6px;
  border-radius: 20px;
  font-size: 10px;
  font-weight: 600;
  white-space: nowrap;
  background: var(--if-accent-soft);
  border: 1px solid color-mix(in srgb, var(--if-accent) 30%, transparent);
  color: var(--if-accent);
`;

const ChevronWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--if-ink-3);
  transition: transform 0.2s ease;
  transform: ${({ expanded }) => (expanded ? 'rotate(180deg)' : 'rotate(0)')};
`;

const ExpandedArea = styled.div`
  display: grid;
  grid-template-columns: ${GRID_COLUMNS};
  padding: 0 14px;
  background: ${EXPANDED_BG};
  border-bottom: 1px solid var(--if-border);
`;

const ExpandedContent = styled.div`
  grid-column: 4 / -1;
  padding: 12px 0 16px;
`;

export default function HistoryRow({
  row,
  isExpanded,
  showSessionBorder,
  isEven,
  onToggle,
}) {
  const {
    author,
    date,
    fieldName,
    from,
    to,
    type,
    category,
    lifecycleEvent,
    srmAccountId,
    propagation,
  } = row;

  return (
    <>
      <Row
        isEven={isEven}
        isExpanded={isExpanded}
        showSessionBorder={showSessionBorder}
        onClick={onToggle}
      >
        <Cell>
          <Timestamp>{formatDayAndTime(date)}</Timestamp>
          <RelativeTime>{timeAgo(date)}</RelativeTime>
        </Cell>
        <Cell>
          <Tooltip
            target={
              <span>
                <UserAvatar name={author} size={24} />
              </span>
            }
            placement="top"
          >
            {author}
          </Tooltip>
        </Cell>
        <Cell>
          {lifecycleEvent ? (
            <>
              <LifecycleEventLabel event={lifecycleEvent}>
                {LIFECYCLE_LABELS[lifecycleEvent]}
              </LifecycleEventLabel>
              {srmAccountId && <SrmAccountId>SRM #{srmAccountId}</SrmAccountId>}
            </>
          ) : (
            <FieldName field={fieldName} />
          )}
          {propagation && (
            <Tooltip
              target={
                <span>
                  <PropagatedBadge>{t`Auto-synced`}</PropagatedBadge>
                </span>
              }
              placement="top"
            >
              {propagationSummary(propagation)}
            </Tooltip>
          )}
        </Cell>
        <Cell>
          <SmartDiff
            change={{ from, to, type }}
            compact
            diffStyle="simple"
            fieldName={fieldName}
          />
        </Cell>
        <Cell>
          <CategoryBadge category={category} />
        </Cell>
        <Cell>
          <ChevronWrapper expanded={isExpanded}>
            <ChevronDown size="small" />
          </ChevronWrapper>
        </Cell>
      </Row>
      {isExpanded && (
        <ExpandedArea>
          <ExpandedContent>
            <CopyButton value={`${formatValue(from)} → ${formatValue(to)}`} />
            <SmartDiff change={{ from, to, type }} fieldName={fieldName} />
          </ExpandedContent>
        </ExpandedArea>
      )}
    </>
  );
}

HistoryRow.propTypes = {
  row: PropTypes.shape({
    author: PropTypes.string.isRequired,
    date: PropTypes.string.isRequired,
    fieldName: PropTypes.string.isRequired,
    from: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.bool,
      PropTypes.object,
      PropTypes.array,
    ]),
    to: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.bool,
      PropTypes.object,
      PropTypes.array,
    ]),
    type: PropTypes.string.isRequired,
    category: PropTypes.string.isRequired,
    lifecycleEvent: PropTypes.string,
    srmAccountId: PropTypes.string,
    propagation: PropTypes.shape({
      action: PropTypes.string,
      sharedSettingName: PropTypes.string,
    }),
  }).isRequired,
  isExpanded: PropTypes.bool.isRequired,
  showSessionBorder: PropTypes.bool.isRequired,
  isEven: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
};
